import { http } from "@/utils/http";
import { faker } from "@faker-js/faker";
import axios from "axios";
import { getToken } from "@/utils/auth";
import { HTTP_HEADERS } from "@/config/httpHead";

type Result = {
  success: boolean;
  data?: Array<any>;
};

type ResultTable = {
  success: boolean;
  data?: {
    /** 列表数据 */
    list: Array<any>;
    /** 总条目数 */
    total?: number;
    /** 每页显示条目个数 */
    pageSize?: number;
    /** 当前页数 */
    currentPage?: number;
  };
};

/** 获取系统管理-用户管理列表 */
export const getUserList = async (data?: object) => {
  // 生成 20 条用户数据
  const list = Array.from({ length: 20 }).map((_, idx) => ({
    id: idx + 1,
    avatar: faker.image.avatar(),
    username: faker.internet.userName(),
    nickname: faker.person.fullName(),
    sex: faker.number.int({ min: 0, max: 1 }),
    dept: {
      id: faker.number.int({ min: 100, max: 110 }),
      name: faker.commerce.department()
    },
    phone: faker.phone.number(),
    status: faker.number.int({ min: 0, max: 1 }),
    remark: faker.lorem.sentence(),
    createTime: faker.date.past().getTime()
  }));
  return Promise.resolve({
    success: true,
    data: {
      list,
      total: list.length,
      pageSize: 10,
      currentPage: 1
    }
  });
};

/** 系统管理-用户管理-获取所有角色列表 */
export const getAllRoleList = () => {
  return http.request<Result>("get", "/list-all-role");
};

/** 系统管理-用户管理-根据userId，获取对应角色id列表（userId：用户id） */
export const getRoleIds = (data?: object) => {
  return http.request<Result>("post", "/list-role-ids", { data });
};

/** 获取系统管理-角色管理列表 */
export const getRoleList = (data?: object) => {
  return http.request<ResultTable>("post", "/role", { data });
};

/** 获取系统管理-菜单管理列表 */
export const getMenuList = (data?: object) => {
  return http.request<Result>("post", "/menu", { data });
};

/** 获取系统管理-部门管理列表 */
export const getDeptList = (data?: object) => {
  return http.request<Result>("post", "/dept", { data });
};

/** 获取系统监控-在线用户列表 */
export const getOnlineLogsList = async (data?: object) => {
  const list = Array.from({ length: 10 }).map((_, idx) => ({
    id: idx + 1,
    username: faker.internet.userName(),
    ip: faker.internet.ipv4(),
    address: faker.location.city(),
    system: faker.helpers.arrayElement(["macOS", "Windows", "Linux"]),
    browser: faker.internet.userAgent(),
    loginTime: faker.date.recent().toISOString()
  }));
  return Promise.resolve({
    success: true,
    data: {
      list,
      total: list.length,
      pageSize: 10,
      currentPage: 1
    }
  });
};

/** 获取系统监控-登录日志列表 */
export const getLoginLogsList = async (data?: object) => {
  const list = Array.from({ length: 10 }).map((_, idx) => ({
    id: idx + 1,
    username: faker.internet.userName(),
    ip: faker.internet.ipv4(),
    address: faker.location.city(),
    system: faker.helpers.arrayElement(["macOS", "Windows", "Linux"]),
    browser: faker.internet.userAgent(),
    status: faker.number.int({ min: 0, max: 1 }),
    behavior: faker.lorem.words(2),
    loginTime: faker.date.recent().toISOString()
  }));
  return Promise.resolve({
    success: true,
    data: {
      list,
      total: list.length,
      pageSize: 10,
      currentPage: 1
    }
  });
};

/** 获取系统监控-操作日志列表 */
export const getOperationLogsList = async (data?: object) => {
  const list = Array.from({ length: 10 }).map((_, idx) => ({
    id: idx + 1,
    username: faker.internet.userName(),
    ip: faker.internet.ipv4(),
    address: faker.location.city(),
    system: faker.helpers.arrayElement(["macOS", "Windows", "Linux"]),
    browser: faker.internet.userAgent(),
    status: faker.number.int({ min: 0, max: 1 }),
    summary: faker.lorem.words(3),
    module: faker.commerce.department(),
    operatingTime: faker.date.recent().toISOString()
  }));
  return Promise.resolve({
    success: true,
    data: {
      list,
      total: list.length,
      pageSize: 10,
      currentPage: 1
    }
  });
};

/** 获取系统监控-系统日志列表 */
export const getSystemLogsList = async (data?: object) => {
  const list = Array.from({ length: 10 }).map((_, idx) => ({
    id: idx + 1,
    level: faker.number.int({ min: 0, max: 4 }),
    module: faker.commerce.department(),
    url: `/${faker.lorem.slug()}`,
    method: faker.helpers.arrayElement(["get", "post"]),
    ip: faker.internet.ipv4(),
    address: faker.location.city(),
    system: faker.helpers.arrayElement(["macOS", "Windows", "Linux"]),
    browser: faker.internet.userAgent(),
    takesTime: faker.number.int({ min: 10, max: 2000 }),
    requestTime: faker.date.recent().toISOString()
  }));
  return Promise.resolve({
    success: true,
    data: {
      list,
      total: list.length,
      pageSize: 10,
      currentPage: 1
    }
  });
};

/** 获取系统监控-系统日志-根据 id 查日志详情 */
export const getSystemLogsDetail = (data?: object) => {
  return http.request<Result>("post", "/system-logs-detail", { data });
};

/** 获取角色管理-权限-菜单权限 */
export const getRoleMenu = (data?: object) => {
  return http.request<Result>("post", "/role-menu", { data });
};

/** 获取角色管理-权限-菜单权限-根据角色 id 查对应菜单 */
export const getRoleMenuIds = (data?: object) => {
  return http.request<Result>("post", "/role-menu-ids", { data });
};

/** 已读消息 */
export const notificationUpdate = (id?: string) => {
  return http.request<Result>("post", `/client/notification/markAsRead/${id}`);
};
/** 获取未读通知数量 */
export const unreadCountNotification = (params?: object) => {
  return http.request<Result>("get", `/client/notification/unreadCount`, {
    params
  });
};
/** 获取消息列表 */
export const getnotificationList = (params?: object) => {
  return http.request<Result>("get", `/client/notification/list`, {
    params
  });
};

/**
 * 获取所在国家地区
 */
export const getCountryCode = () => {
  return axios.get<any, any>("/country/api/country-code");
};

/**
 * 上传图片至oss
 */
export const uploadImageToOss = (formData: FormData) => {
  const token = getToken();
  return axios.post(
    `${HTTP_HEADERS.Http}/video-project-image/upload`,
    formData,
    {
      headers: {
        "Content-Type": "multipart/form-data",
        Authorization: token,
        "X-Access-Token": token
      }
    }
  );
};

/**
 * 删除oss图片
 */
export const deleteOssImage = (data?: any) => {
  const token = getToken();
  return http.request<Result>(
    "post",
    `/video-portrait/deleteOss?url=${data?.url}`,
    {
      headers: {
        Authorization: token,
        "X-Access-Token": token
      }
    }
  );
};

/**
 * 获取所有语言列表
 */
export const getAllLanguageList = () => {
  return http.request<any>("get", `/product/supportedLanguages`);
};
